import { DEFAULT_BACKUP_URL } from '../db/database';

const DEVICE_ID_KEY = 'scopeshield_device_id';
const SESSION_TOKEN_KEY = 'scopeshield_session_token';
const SESSION_EXPIRY_KEY = 'scopeshield_session_expiry';

let authRequiredCallbacks = [];
let reconciledSessionToken = null;

export class AuthError extends Error {
  constructor(message, code, authRequired, providerHttpStatus) {
    super(message);
    this.name = 'AuthError';
    this.code = code;
    this.authRequired = authRequired;
    this.providerHttpStatus = providerHttpStatus;
  }
}

const resolveUrl = (settings) => settings?.googleBackupUrl || DEFAULT_BACKUP_URL;

export function getDeviceId() {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}

export function getActiveSessionToken() {
  const token = localStorage.getItem(SESSION_TOKEN_KEY);
  const expiry = localStorage.getItem(SESSION_EXPIRY_KEY);
  if (!token || !expiry) return null;
  if (new Date(expiry) <= new Date()) {
    clearSession();
    return null;
  }
  return token;
}

export function clearSession() {
  localStorage.removeItem(SESSION_TOKEN_KEY);
  localStorage.removeItem(SESSION_EXPIRY_KEY);
  reconciledSessionToken = null;
}

function setSession(token, expiry) {
  localStorage.setItem(SESSION_TOKEN_KEY, token);
  localStorage.setItem(SESSION_EXPIRY_KEY, expiry);
}

export function markCurrentSessionReconciled() {
  const token = getActiveSessionToken();
  if (token) reconciledSessionToken = token;
}

export function hasCurrentSessionBeenReconciled() {
  const token = getActiveSessionToken();
  return !!token && reconciledSessionToken === token;
}

export function refreshActiveSessionExpiry(serverExpiry) {
  const token = localStorage.getItem(SESSION_TOKEN_KEY);
  if (!token || !serverExpiry) return;
  const expiryDate = new Date(serverExpiry);
  if (isNaN(expiryDate.getTime()) || expiryDate <= new Date()) return;
  localStorage.setItem(SESSION_EXPIRY_KEY, serverExpiry);
}

export function onAuthRequired(callback) {
  authRequiredCallbacks.push(callback);
  return () => {
    authRequiredCallbacks = authRequiredCallbacks.filter(cb => cb !== callback);
  };
}

export function triggerAuthRequired() {
  clearSession();
  authRequiredCallbacks.forEach(cb => cb());
}

const AUTH_STATUS_TIMEOUT_MS = 45000;
const LOGIN_TIMEOUT_MS = 45000;
const LOGIN_RECONCILE_TIMEOUT_MS = 6000;
const VALIDATE_SESSION_TIMEOUT_MS = 45000;
const LOGOUT_TIMEOUT_MS = 30000;

const EXPLICIT_AUTH_FAILURE_CODES = new Set([
  'AUTH_REQUIRED',
  'SESSION_INVALID',
  'SESSION_EXPIRED',
  'SESSION_REVOKED',
  'AUTH_NOT_CONFIGURED',
]);

function isExplicitAuthFailure(data) {
  if (!data || typeof data !== 'object') return false;
  if (data.authRequired === true) return true;
  if (data.code && EXPLICIT_AUTH_FAILURE_CODES.has(data.code)) return true;
  return false;
}

async function authFetch(url, options = {}) {
  const controller = new AbortController();
  const timeoutMs = options.timeoutMs || AUTH_STATUS_TIMEOUT_MS;
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const fetchOptions = {
      method: options.method || 'GET',
      signal: controller.signal,
    };
    if (options.headers) fetchOptions.headers = options.headers;
    if (options.body) fetchOptions.body = options.body;

    const response = await fetch(url, fetchOptions);
    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`Server returned ${response.status} ${response.statusText}`);
    }

    const text = await response.text();
    if (!text || text.trim().length === 0) {
      throw new Error('Server returned an empty response. Check your Apps Script deployment.');
    }
    if (text.trim().startsWith('<') || text.trim().startsWith('<!')) {
      throw new Error('Server returned HTML instead of JSON. Check your Apps Script URL.');
    }

    let data;
    try {
      data = JSON.parse(text);
    } catch {
      throw new Error('Server returned invalid JSON. Check your Apps Script deployment.');
    }

    if (data && typeof data === 'object') {
      if (data.success === false) {
        const code = data.code || undefined;
        const authRequired = data.authRequired === true;
        const providerHttpStatus = data.providerHttpStatus || undefined;
        const err = new Error(data.error || data.message || 'Request failed');
        if (code) err.code = code;
        if (authRequired) err.authRequired = authRequired;
        if (providerHttpStatus) err.providerHttpStatus = providerHttpStatus;
        throw err;
      }
      if (data.error && data.success !== true) {
        const code = data.code || undefined;
        const authRequired = data.authRequired === true;
        const providerHttpStatus = data.providerHttpStatus || undefined;
        const err = new Error(data.error || 'Request returned an error');
        if (code) err.code = code;
        if (authRequired) err.authRequired = authRequired;
        if (providerHttpStatus) err.providerHttpStatus = providerHttpStatus;
        throw err;
      }

      const serverExpiry = data.sessionExpiresAt || data.expiresAt;
      if (serverExpiry) {
        refreshActiveSessionExpiry(serverExpiry);
      }
    }

    return data;
  } catch (err) {
    clearTimeout(timeoutId);
    if (err.name === 'AbortError') {
      throw new Error(`Request timed out after ${Math.round(timeoutMs / 1000)} seconds.`);
    }
    throw err;
  }
}

export async function authStatus(settings) {
  const url = resolveUrl(settings);
  if (!url) return { authEnabled: false };
  const data = await authFetch(`${url}?action=authStatus`, { timeoutMs: AUTH_STATUS_TIMEOUT_MS });
  if (data.authEnabled === false || data.enabled === false || data.authenticationRequired === false) {
    return { authEnabled: false };
  }
  if (data.authEnabled === true || data.enabled === true || data.authenticationRequired === true) {
    return { authEnabled: true };
  }
  throw new Error('Server returned an ambiguous authentication status.');
}

export async function login(settings, accessCode) {
  const url = resolveUrl(settings);
  if (!url) throw new Error('No cloud endpoint configured.');
  const data = await authFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
    body: JSON.stringify({
      action: 'login',
      accessCode,
      deviceId: getDeviceId(),
    }),
    timeoutMs: LOGIN_TIMEOUT_MS,
  });

  const isSuccess = data && (data.success === true || data.status === 'ok' || data.authenticated === true);
  if (!isSuccess) {
    const code = data?.code || undefined;
    const authRequired = data?.authRequired === true;
    const err = new Error(data?.error || data?.message || 'Login failed. Please check your access code.');
    if (code) err.code = code;
    if (authRequired) err.authRequired = authRequired;
    throw err;
  }

  const sessionToken = data.sessionToken || data.token;
  const expiresAt = data.expiresAt || data.expiry || data.tokenExpiry;
  if (!sessionToken) {
    throw new Error('Login succeeded but no session token was returned.');
  }

  const fallbackExpiry = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
  setSession(sessionToken, expiresAt || fallbackExpiry);

  // Reconcile before the application leaves the login state so an existing
  // local workspace cannot become interactive while cloud recovery is still
  // modifying IndexedDB. A transient cloud failure never invalidates login.
  let reconciliation = null;
  try {
    const { reconcileLocalWorkspaceWithCloud, pushWorkspaceBackup } = await import('./cloudBackup');
    reconciliation = await reconcileLocalWorkspaceWithCloud(settings, { timeoutMs: LOGIN_RECONCILE_TIMEOUT_MS });
    markCurrentSessionReconciled();

    // Once reconciliation has completed, create/update the protected snapshot
    // in the background so a slow backup cannot hold the login screen open.
    void pushWorkspaceBackup(settings).catch((syncErr) => {
      console.warn('ScopeShield: Automatic post-login backup was deferred; session preserved.', syncErr);
    });
  } catch (syncErr) {
    console.warn('ScopeShield: Automatic post-login reconciliation was unavailable; session preserved.', syncErr);
  }

  return {
    success: true,
    sessionToken,
    expiresAt: expiresAt || fallbackExpiry,
    workspaceReconciled: reconciliation?.reconciled === true,
  };
}

export async function validateSession(settings) {
  const token = getActiveSessionToken();
  if (!token) return { valid: false };

  const url = resolveUrl(settings);
  if (!url) return { valid: false };

  try {
    const data = await authFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      body: JSON.stringify({
        action: 'validateSession',
        sessionToken: token,
      }),
      timeoutMs: VALIDATE_SESSION_TIMEOUT_MS,
    });

    const isValid = data && (data.success === true || data.status === 'ok' || data.valid === true);
    if (isValid) {
      const newExpiry = data.expiresAt || data.expiry || data.sessionExpiresAt;
      if (newExpiry) setSession(token, newExpiry);
      return { valid: true };
    }

    if (isExplicitAuthFailure(data)) {
      clearSession();
      return { valid: false };
    }

    throw new Error(data?.error || data?.message || 'Session validation returned an unexpected response.');
  } catch (err) {
    const code = err.code;
    const authRequired = err.authRequired === true;
    if (authRequired || (code && EXPLICIT_AUTH_FAILURE_CODES.has(code))) {
      clearSession();
      return { valid: false };
    }

    throw err;
  }
}

export async function logout(settings) {
  const token = getActiveSessionToken();
  if (token) {
    const url = resolveUrl(settings);
    if (url) {
      try {
        await authFetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
          body: JSON.stringify({
            action: 'logout',
            sessionToken: token,
          }),
          timeoutMs: LOGOUT_TIMEOUT_MS,
        });
      } catch {
        // Ignore logout network errors — clear locally regardless
      }
    }
  }
  clearSession();
  triggerAuthRequired();
}